import { AgSparklineOptions } from 'ag-charts-community';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDownloadCsvMutation } from '../../../../hooks/mutations/useDownloadMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { DataItem, Report } from '../../../../services/apiSLTP/activities';
import { DailyTruckRide } from '../../../../services/apiSLTP/dailyReports/dailyTrucksRides';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getReportStateColor, getReportTypeColor } from '../../../../utils/colors';
import { createDateTimeColumn, createTimeColumn, formatDate, formatDateToDDMMYYYY, formatTime } from '../../../../utils/dates';
import { activityStateEnum, exportTypeEnum, getReportStateInfos, getReportTypeInfos, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import ExportCsvIcon from '../../../ExportCsvIcon';
import GenericAgGridTable from '../../GenericAgGridTable';
import { stepModeEnum } from '../../../../constants';

interface WorkersTableProps {
    activities: DataItem[];
    dailyTrucksRides: DailyTruckRide[];
    currentDate: Date;
    reports: Report[];
    stepMode: number;
}

const WorkersTable: React.FC<WorkersTableProps> = ({ activities, dailyTrucksRides, currentDate, reports, stepMode }) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const { mutate: exportCsv, isLoading: isExportCsvLoading } = useDownloadCsvMutation();

    const globalMax = useMemo(() => {
        const counts = reports.map(
            (report) => activities.filter((a) => a.reportId._id === report._id && a.activityState === activityStateEnum.REPORTFILLED).length
        );
        return Math.max(...counts);
    }, [reports, activities]);

    const sortActivitiesByPerson = () => {
        return reports
            .filter((value) => !value.userId.isSpecial)
            .sort((a, b) => {
                if (formatName(a.userId.firstName, a.userId.lastName) > formatName(b.userId.firstName, b.userId.lastName)) return 1;
                if (formatName(a.userId.firstName, a.userId.lastName) < formatName(b.userId.firstName, b.userId.lastName)) return -1;
                return 0;
            });
    };

    const columnDefs: ColDef<Report>[] = [
        {
            headerName: capitalizeFirstLetter(t('date')),
            valueGetter: () => formatDateToDDMMYYYY(currentDate, '/'),
            colId: 'date',
            initialHide: true,
        },
        {
            valueGetter: (params) => formatName(params.data?.userId.firstName, params.data?.userId.lastName),
            headerName: capitalizeFirstLetter(t('name')),
            colId: 'name',
            minWidth: 200,
            initialFlex: 1,
        },
        {
            field: 'userId.codeProC',
            headerName: capitalizeFirstLetter(t('codeProC')),
            colId: 'codeProC',
        },
        {
            field: 'workStartTime',
            headerName: capitalizeFirstLetter(t('workStartTime')),
            colId: 'workStartTime',
            valueFormatter: (params) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                if (isNaN(date.getTime())) return '';
                return formatTime(date);
            },
        },
        {
            field: 'workEndTime',
            headerName: capitalizeFirstLetter(t('workEndTime')),
            colId: 'workEndTime',
            valueFormatter: (params) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                if (isNaN(date.getTime())) return '';
                return formatTime(date);
            },
        },
        {
            headerName: capitalizeFirstLetter(t('overTimeAuthorizedBy')),
            colId: 'overTimeValidatedBy',
            initialWidth: 300,
            field: 'overTimeValidatedBy.username',
        },
        {
            headerName: capitalizeFirstLetter(t('vehicle')),
            colId: 'vehicle',
            field: 'vehicleId.designation',
        },
        {
            headerName: capitalizeFirstLetter(t('truck')),
            colId: 'truck',
            field: 'truckId.designation',
        },
        {
            headerName: capitalizeFirstLetter(t('state')),
            valueGetter: (params) => capitalizeFirstLetter(t(getReportTypeInfos(params.data?.reportType))),
            cellRenderer: (params: ICellRendererParams<Report>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getReportTypeColor(params.data?.reportType)} text={t(getReportTypeInfos(params.data?.reportType))} />;
            },
            colId: 'reportType',
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getReportStateInfos(params.data?.reportState))),
            cellRenderer: (params: ICellRendererParams<Report>) => {
                if (!params.data) return params.value;
                return (
                    <StateCell stateColor={getReportStateColor(params.data?.reportState)} text={t(getReportStateInfos(params.data?.reportState))} />
                );
            },
            colId: 'reportState',
        },
        createDateTimeColumn<Report>('reportSubmitValidationTimeStamp', 'submittedThe'),
        /*{
            headerName: capitalizeFirstLetter(t('activitiesSent')),
            colId: 'activitySentSparkline',
            cellRenderer: 'agSparklineCellRenderer',
            valueGetter: (params) => {
                const reportId = params.data?._id;
                const relatedActivities = activities.filter((a) => a.reportId._id === reportId);
                const filledCount = relatedActivities.filter((a) => a.activityState === activityStateEnum.REPORTFILLED).length;
                const total = relatedActivities.length;
                const ratio = total > 0 ? (filledCount / total) * 100 : 0;
                return [ratio];
            },
            cellRendererParams: {
                sparklineOptions: {
                    type: 'bar',
                    direction: 'horizontal',

                    min: 0,
                    max: 100,
                } as AgSparklineOptions,
            },
        },*/
        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<Report>[]),
    ];

    return (
        <GenericAgGridTable
            rowData={sortActivitiesByPerson()}
            columnDefs={columnDefs}
            exportCsvFileName={`supervision-workers-${formatDate(currentDate)}`}
            agGridProps={{ getRowId: ({ data }) => data._id }}
            headerRightComponent={
                <>
                    <ExportCsvIcon
                        initDate={currentDate}
                        onExport={(startDate, endDate, fileName, delimiter) =>
                            exportCsv({ exportType: exportTypeEnum.REPORTS, queries: { startDate, endDate, delimiter, fileName } })
                        }
                        exportIsLoading={isExportCsvLoading}
                        subTitle={capitalizeFirstLetter(t('reports'))}
                    />
                </>
            }
        />
    );
};

export default WorkersTable;
